import React from 'react';
import PropTypes from 'prop-types';
import { LuTrash2, LuUsers } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import useSmoText from '../smoText';
import CityCenterGroups from './CityCenterGroups';

/**
 * Header of the open SMO's team: name, backup-view badge, "SMO'yu Kaldır" and
 * the SMO's centres grouped by city (and by district above 10 centres).
 * Every city / district starts collapsed.
 */
const SmoCenters = ({ selectedSMO, smoCenters, collapsedCities, setCollapsedCities, canRemoveSMO, onRemoveSmo }) => {
  const { label } = useSmoText();

  // Şehirlere göre grupla
  const cityGroups = {};
  smoCenters.forEach((center) => {
    const city = center.city || label('centers.unknownCity');
    if (!cityGroups[city]) cityGroups[city] = [];
    cityGroups[city].push(center);
  });
  // 0 merkezli şehirleri filtrele
  const groups = Object.keys(cityGroups)
    .filter((city) => cityGroups[city].length > 0)
    .sort((a, b) => a.localeCompare(b, 'tr'))
    .map((city) => ({ city, centers: cityGroups[city] }));

  return (
    <div className="tw-space-y-3">
      <div className="tw-flex tw-flex-wrap tw-items-start tw-gap-3">
        <GlassIcon gradient="tw-from-teal-400 tw-to-emerald-600 tw-shadow-teal-500/30" size="xs" className="tw-mt-0.5">
          <LuUsers aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
        <div className="tw-min-w-[12rem] tw-flex-1">
          <h2 className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-lg tw-font-semibold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">
            {label('team.title', { name: `${selectedSMO.name} ${selectedSMO.surname}` })}
            {selectedSMO.delegatedFromBackup && (
              <span title={label('team.projectBackupHint')} className="tw-rounded-full tw-bg-amber-400/20 tw-px-2.5 tw-py-0.5 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-amber-800 dark:tw-text-amber-200">
                {label('team.projectBackup', { project: selectedSMO.restrictToProjectTitle })}
              </span>
            )}
            {selectedSMO.delegatedFromCenterBackup && (
              <span title={label('team.centerBackupHint')} className="tw-rounded-full tw-bg-amber-400/20 tw-px-2.5 tw-py-0.5 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-amber-800 dark:tw-text-amber-200">
                {label('team.centerBackup', { count: selectedSMO.centerCount })}
              </span>
            )}
          </h2>
          <p className="tw-mt-0.5 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('team.subtitle')}</p>
        </div>
        {/* Admin-only: remove this SMO (clears the SMO assignment on its centers) */}
        {canRemoveSMO && !selectedSMO.delegatedFromBackup && !selectedSMO.delegatedFromCenterBackup && (
          <Button size="sm" variant="danger" onClick={onRemoveSmo} title={label('team.removeSmoHint')}>
            <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('team.removeSmo')}
          </Button>
        )}
      </div>

      <CityCenterGroups
        groups={groups}
        keyPrefix={selectedSMO.id}
        collapsedCities={collapsedCities}
        setCollapsedCities={setCollapsedCities}
        showRole
      />
    </div>
  );
};

SmoCenters.propTypes = {
  selectedSMO: PropTypes.object.isRequired,
  smoCenters: PropTypes.array.isRequired,
  collapsedCities: PropTypes.object.isRequired,
  setCollapsedCities: PropTypes.func.isRequired,
  canRemoveSMO: PropTypes.bool,
  onRemoveSmo: PropTypes.func.isRequired,
};

export default SmoCenters;
